import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { COMPANY } from "@/data";

const LINKS = [
  { href: "#start", label: "Start" },
  { href: "#leistungen", label: "Leistungen", testId: "nav-link-leistungen" },
  { href: "#ueber-uns", label: "Über uns", testId: "nav-link-ueber-uns" },
  { href: "#preise", label: "Preise", testId: "nav-link-preise" },
  { href: "#preisschaeizer", label: "Preisschätzer", testId: "nav-link-preisschaeizer" },
  { href: "#galerie", label: "Galerie", testId: "nav-link-galerie" },
  { href: "#kontakt", label: "Kontakt", testId: "nav-link-kontakt" },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b bg-white/80 backdrop-blur-md transition-shadow duration-300 ${
        scrolled ? "border-slate-200/80 shadow-[0_1px_0_rgba(15,23,42,0.04),0_8px_30px_-12px_rgba(15,23,42,0.12)]" : "border-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a href="#start" data-testid="nav-brand-logo" className="group flex items-center gap-3">
          <img src="/images/pureglass-logo.jpeg" alt="PureGlass Witten Logo" className="h-12 w-auto" />
          <span className="flex flex-col leading-tight">
            <span className="font-serif text-lg font-semibold tracking-tight text-slate-900">
              PureGlass <span className="text-sky-700">Witten</span>
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
              {COMPANY.tagline} · {COMPANY.founded}
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-5 lg:flex" aria-label="Hauptnavigation">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testId || `nav-link-${l.label.toLowerCase()}`}
              className="whitespace-nowrap text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-sky-700"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={COMPANY.phoneHref}
            data-testid="nav-phone-badge"
            className="flex items-center gap-2 whitespace-nowrap rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 transition-colors duration-200 hover:border-sky-300 hover:text-sky-700"
          >
            <Phone className="h-3.5 w-3.5" />
            {COMPANY.phone}
          </a>
          <a
            href="#kontakt"
            data-testid="nav-cta-button"
            className="whitespace-nowrap rounded-full bg-sky-700 px-5 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-sky-800"
          >
            Angebot anfordern
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="rounded-md p-2 text-slate-700 lg:hidden"
          aria-label="Menü öffnen"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-slate-100 bg-white lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="space-y-1 px-4 py-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`${l.testId || "nav-link"}-mobile`}
                  onClick={() => setOpen(false)}
                  className="block rounded-md px-3 py-2.5 text-base font-medium text-slate-700 hover:bg-slate-50"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#kontakt"
                onClick={() => setOpen(false)}
                data-testid="nav-cta-button-mobile"
                className="mt-2 block rounded-full bg-sky-700 px-5 py-3 text-center text-sm font-semibold text-white"
              >
                Angebot anfordern
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
